import { formatNumber } from '@berky/ui'

import { findModule } from '../modules'
import type { UsageDay } from './api'

export interface UsageFilters {
  from: string
  to: string
  moduleId: string
}

/** Calendar dates are shared with the backend's Europe/Berlin reports. */
export function berlinDate(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  return ['year', 'month', 'day']
    .map((type) => parts.find((part) => part.type === type)?.value)
    .join('-')
}

export function berlinDateTime(value: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

export function shiftDate(date: string, days: number): string {
  const result = new Date(`${date}T12:00:00Z`)
  result.setUTCDate(result.getUTCDate() + days)
  return result.toISOString().slice(0, 10)
}

export function initialFilters(): UsageFilters {
  const to = berlinDate()
  return { from: shiftDate(to, -29), to, moduleId: '' }
}

export function validateFilters(filters: UsageFilters): string | null {
  const { from, to } = filters
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(from) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(to) ||
    !Number.isFinite(Date.parse(from)) ||
    !Number.isFinite(Date.parse(to)) ||
    from < '2000-01-01' ||
    from > to ||
    to > berlinDate()
  ) {
    return 'Bitte wählen Sie einen gültigen Zeitraum zwischen dem 01.01.2000 und heute.'
  }
  return (Date.parse(to) - Date.parse(from)) / 86_400_000 >= 366
    ? 'Der Zeitraum darf höchstens 366 Tage umfassen.'
    : null
}

export function queryFilters(filters: UsageFilters) {
  return { von: filters.from, bis: filters.to, modul: filters.moduleId || undefined }
}

export function moduleTitle(id: string): string {
  return id === 'platform'
    ? 'Startseite & Plattformfunktionen'
    : (findModule(id)?.title ?? id)
}

const pageNames: Record<string, string> = {
  ':parameter': 'Detailansicht',
  '*': 'Weitere Seite',
  neu: 'Neu anlegen',
  bearbeiten: 'Bearbeiten',
  aenderung: 'Änderungsantrag',
  uebersicht: 'Übersicht',
  benachrichtigungen: 'Benachrichtigungen',
  einstellungen: 'Einstellungen',
  module: 'Module',
  'module-rechte': 'Module & Rechte',
  navigation: 'Navigation',
  'module-navigation': 'Modulnavigation',
  nutzungsanalyse: 'Nutzungsanalyse',
  'mein-feedback': 'Meine Rückmeldungen',
  feedback: 'Rückmeldungen',
  forbidden: 'Kein Zugriff',
  bedarfe: 'Bedarfe',
  eigenbeleg: 'Eigenbeleg',
  'karten-bargeld': 'Karten & Bargeld',
}

export function pageTitle(path: string, moduleId: string): string {
  if (path === '/') return moduleId === 'platform' ? 'Startseite' : 'Übersicht'
  return path
    .split('/')
    .filter(Boolean)
    .map(
      (segment) =>
        pageNames[segment] ??
        `${segment.charAt(0).toLocaleUpperCase('de')}${segment.slice(1).replaceAll('-', ' ')}`,
    )
    .join(' · ')
}

export function duration(seconds: number): string {
  return seconds < 60
    ? `${formatNumber(seconds)} Sek.`
    : `${formatNumber(seconds / 60, 1)} Min.`
}

export function comparison(current: number, previous: number): string {
  if (previous === 0)
    return current === 0 ? 'Auch zuvor keine Nutzung' : 'Zuvor keine Nutzung'
  const percent = ((current - previous) / previous) * 100
  return `${percent > 0 ? '+' : ''}${formatNumber(percent, 0)} % zum Vergleichszeitraum`
}

export function completeDays(from: string, to: string, days: UsageDay[]): UsageDay[] {
  const indexed = new Map(days.map((day) => [day.date, day]))
  const count = Math.min(
    366,
    Math.max(0, (Date.parse(to) - Date.parse(from)) / 86_400_000 + 1),
  )
  return Array.from({ length: count }, (_, index) => {
    const date = shiftDate(from, index)
    return indexed.get(date) ?? { date, users: 0, entries: 0 }
  })
}
